// FTAEditorPage.tsx — Enhanced Fault Tree & Block Diagram Editor for Magnus Resolve
// Architecture: main canvas + extracted sub-components (CC ≤ 15 each)
// Features: Full IEC 61025 / AIAG-VDA symbols • Basic/Colorful modes • Gen AI • Dark mode

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import ReactFlow, {
    Background, Controls, MiniMap, Panel,
    ReactFlowProvider, useReactFlow,
    useNodesState, useEdgesState, addEdge, Connection, EdgeTypes,
} from 'reactflow';
import 'reactflow/dist/style.css';
import clsx from 'clsx';
import EnhancedFTANode from '../components/fta/EnhancedFTANode';
import CustomFTAEdge from '../components/fta/CustomFTAEdge';
import { FTANodeType, FTANodeData, InterfaceType } from '../types/fta.types';
import { getLayoutedElements, calculateProbabilities } from '../utils/fta/ftalogic';
import { generateFTATree } from '../services/fta/geminiService';
import { industryTemplates } from '../utils/fta/industryTemplates';
import { ActionPlanTable } from '../components/fta/ActionPlanTable';
import { PropertiesPanel } from '../components/fta/PropertiesPanel';
import FTAGenAIBar from '../components/fta/FTAGenAIBar';
import FTAToolbar, { DisplayMode } from '../components/fta/FTAToolbar';
import FTANodePalette from '../components/fta/FTANodePalette';
import FTALegend from '../components/fta/FTALegend';
import { TYPE_COLORS } from '../components/fta/ftaPaletteConfig';

// ── Stable refs (outside component) ──────────────────────────────────────────
const nodeTypes = { ftaNode: EnhancedFTANode };
const edgeTypes: EdgeTypes = { customEdge: CustomFTAEdge };

const INITIAL_NODES = [{
    id: 'root',
    type: 'ftaNode' as const,
    position: { x: 200, y: 80 },
    data: { label: 'System Failure', type: FTANodeType.TOP_EVENT, displayMode: 'colorful' } as FTANodeData,
}];

// ── Editor (lean render, logic here, UI in sub-components) ────────────────────
function FTAEditorInner() {
    const reactFlow = useReactFlow();

    const [nodes, setNodes, onNodesChange] = useNodesState(INITIAL_NODES);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
    const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
    const [displayMode, setDisplayMode] = useState<DisplayMode>('colorful');
    const [isDark, setIsDark] = useState(false);
    const [view, setView] = useState<'diagram' | 'table'>('diagram');
    const [prompt, setPrompt] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [aiError, setAiError] = useState<string | null>(null);
    const [diagramName, setDiagramName] = useState('Fault Tree Analysis');
    const [history, setHistory] = useState([{ nodes: INITIAL_NODES as any[], edges: [] as any[] }]);
    const [histIndex, setHistIndex] = useState(0);
    const pushingRef = useRef(false);

    // Sync display mode into all node data when it changes
    useEffect(() => {
        setNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, displayMode } })));
    }, [displayMode, setNodes]);

    // ── History ───────────────────────────────────────────────────────────
    function pushHistory(ns: any[], es: any[]) {
        if (pushingRef.current) return;
        setHistory(h => [...h.slice(0, histIndex + 1), { nodes: ns, edges: es }]);
        setHistIndex(i => i + 1);
    }

    function handleUndo() {
        if (histIndex <= 0) return;
        const prev = history[histIndex - 1];
        pushingRef.current = true;
        setNodes(prev.nodes); setEdges(prev.edges); setHistIndex(i => i - 1);
        setTimeout(() => { pushingRef.current = false; }, 50);
    }

    function handleRedo() {
        if (histIndex >= history.length - 1) return;
        const next = history[histIndex + 1];
        pushingRef.current = true;
        setNodes(next.nodes); setEdges(next.edges); setHistIndex(i => i + 1);
        setTimeout(() => { pushingRef.current = false; }, 50);
    }

    // ── Canvas interactions ───────────────────────────────────────────────
    const onConnect = useCallback((params: Connection) => {
        setEdges(eds => {
            const updated = addEdge({ ...params, type: 'customEdge', data: { interfaceType: InterfaceType.DEFAULT } }, eds);
            pushHistory(nodes, updated);
            return updated;
        });
    }, [nodes, setEdges]); // eslint-disable-line

    // ── Add node from palette ─────────────────────────────────────────────
    function handleAddNode(type: FTANodeType) {
        const id = `n-${Date.now()}`;
        const center = reactFlow.project({ x: window.innerWidth / 2 - 80, y: window.innerHeight / 2 });
        const label = type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        const newNode = {
            id, type: 'ftaNode' as const,
            position: { x: center.x + Math.random() * 60 - 30, y: center.y + Math.random() * 60 - 30 },
            data: {
                label, type,
                displayMode,
                probability: type === FTANodeType.BASIC_EVENT ? 0.01 : undefined,
            } as FTANodeData,
        };
        setNodes(nds => { const u = [...nds, newNode]; pushHistory(u, edges); return u; });
    }

    // ── Gen AI ────────────────────────────────────────────────────────────
    async function handleGenerate() {
        if (!prompt.trim()) return;
        setIsGenerating(true); setAiError(null);
        try {
            const tree = await generateFTATree(prompt);
            const layouted = getLayoutedElements(tree.nodes, tree.edges);
            const withMode = layouted.nodes.map((n: any) => ({ ...n, data: { ...n.data, displayMode } }));
            setNodes(withMode as any);
            const newEdges = layouted.edges.map((e: any) => ({ ...e, type: 'customEdge', data: { interfaceType: InterfaceType.DEFAULT } }));
            setEdges(newEdges);
            pushHistory(withMode, newEdges);
            setTimeout(() => reactFlow.fitView({ padding: 0.15 }), 100);
        } catch (err: any) {
            setAiError(err.message || 'AI generation failed');
        } finally { setIsGenerating(false); }
    }

    // ── Toolbar actions ───────────────────────────────────────────────────
    function handleLayout() {
        const l = getLayoutedElements(nodes, edges);
        setNodes(l.nodes as any); setEdges(l.edges);
        setTimeout(() => reactFlow.fitView({ padding: 0.15 }), 50);
    }

    function handleCalculate() { setNodes(calculateProbabilities(nodes, edges) as any); }

    function handleNew() {
        if (nodes.length > 1 && !window.confirm('Clear the current diagram?')) return;
        const fresh = [{ id: `root-${Date.now()}`, type: 'ftaNode' as const, position: { x: 0, y: 0 }, data: { label: 'System Failure', type: FTANodeType.TOP_EVENT, displayMode } as FTANodeData }];
        setNodes(fresh); setEdges([]); reactFlow.setViewport({ x: 0, y: 0, zoom: 1 });
        pushHistory(fresh, []);
    }

    function handleLoadTemplate(key: string) {
        const tmpl = industryTemplates[key];
        if (!tmpl) return;
        const l = getLayoutedElements(tmpl.nodes, tmpl.edges);
        const withMode = l.nodes.map((n: any) => ({ ...n, data: { ...n.data, displayMode } }));
        setNodes(withMode as any);
        const newEdges = l.edges.map((e: any) => ({ ...e, type: 'customEdge', data: { interfaceType: e.data?.interfaceType || InterfaceType.DEFAULT } }));
        setEdges(newEdges);
        pushHistory(withMode, newEdges);
        setTimeout(() => reactFlow.fitView({ padding: 0.15 }), 100);
    }

    function handleExportJSON(name: string) {
        const blob = new Blob([JSON.stringify({ name, nodes, edges }, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `${name.replace(/\s+/g, '_')}.fta.json`; a.click();
        URL.revokeObjectURL(url);
    }

    function handleImport(file: File) {
        const reader = new FileReader();
        reader.onload = ev => {
            try {
                const parsed = JSON.parse(ev.target?.result as string);
                const withMode = (parsed.nodes || []).map((n: any) => ({ ...n, data: { ...n.data, displayMode } }));
                setNodes(withMode); setEdges(parsed.edges || []);
                if (parsed.name) setDiagramName(parsed.name);
                pushHistory(withMode, parsed.edges || []);
                setTimeout(() => reactFlow.fitView({ padding: 0.15 }), 100);
            } catch { alert('Invalid FTA JSON file'); }
        };
        reader.readAsText(file);
    }

    // ── Derived ───────────────────────────────────────────────────────────
    const selectedNode = useMemo(() => nodes.find(n => n.id === selectedNodeId), [nodes, selectedNodeId]);
    const selectedEdge = useMemo(() => edges.find(e => e.id === selectedEdgeId), [edges, selectedEdgeId]);
    const updateSelectedNode = useCallback((key: string, value: any) => {
        if (!selectedNodeId) return;
        setNodes(nds => nds.map(n => n.id === selectedNodeId ? { ...n, data: { ...n.data, [key]: key === 'probability' ? parseFloat(value) : value } } : n));
    }, [selectedNodeId, setNodes]);

    const surface = isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';

    return (
        <div className={clsx('w-full h-full flex flex-col transition-colors', isDark ? 'bg-slate-900' : 'bg-slate-50')}>

            {/* Header: title + Gen AI bar + toolbar */}
            <header className={clsx('h-14 border-b flex items-center gap-3 px-4 shrink-0', surface)}>
                <div className="flex items-center gap-2 shrink-0">
                    <div className="p-1.5 rounded-lg bg-indigo-600">
                        <svg viewBox="0 0 24 24" className="w-4 h-4 text-white fill-current"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
                    </div>
                    <input value={diagramName} onChange={e => setDiagramName(e.target.value)}
                        className={clsx('text-base font-bold bg-transparent border-b border-transparent hover:border-slate-300 focus:border-indigo-500 focus:outline-none px-1 max-w-[180px]', isDark ? 'text-white' : 'text-slate-800')} />
                    <span className={clsx('text-[10px] px-2 py-0.5 rounded font-semibold shrink-0', isDark ? 'bg-indigo-900 text-indigo-300' : 'bg-indigo-50 text-indigo-700')}>
                        IEC 61025 / AIAG-VDA
                    </span>
                </div>

                <FTAGenAIBar
                    prompt={prompt} setPrompt={setPrompt}
                    onGenerate={handleGenerate} isGenerating={isGenerating}
                    isDark={isDark}
                />

                <FTAToolbar
                    isDark={isDark} setIsDark={setIsDark}
                    displayMode={displayMode} setDisplayMode={setDisplayMode}
                    view={view} setView={setView}
                    histIndex={histIndex} histLength={history.length}
                    onUndo={handleUndo} onRedo={handleRedo}
                    onNew={handleNew} onLayout={handleLayout} onCalculate={handleCalculate}
                    onExportJSON={() => handleExportJSON(diagramName)}
                    onImport={handleImport}
                    onLoadTemplate={handleLoadTemplate}
                    diagramName={diagramName}
                />
            </header>

            {aiError && (
                <div className="mx-4 mt-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700">
                    ⚠ {aiError}
                    <button onClick={() => setAiError(null)} className="ml-auto text-red-400 hover:text-red-600">✕</button>
                </div>
            )}

            <div className="flex-1 flex overflow-hidden">
                {/* Left palette */}
                <FTANodePalette
                    isDark={isDark} displayMode={displayMode}
                    onAddNode={handleAddNode}
                />

                {/* Canvas or table */}
                {view === 'diagram' ? (
                    <div className="flex-1 relative">
                        <ReactFlow
                            nodes={nodes} edges={edges}
                            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
                            onConnect={onConnect}
                            onNodeClick={(_, n) => { setSelectedNodeId(n.id); setSelectedEdgeId(null); }}
                            onEdgeClick={(_, e) => { setSelectedEdgeId(e.id); setSelectedNodeId(null); }}
                            onPaneClick={() => { setSelectedNodeId(null); setSelectedEdgeId(null); }}
                            nodeTypes={nodeTypes} edgeTypes={edgeTypes}
                            fitView
                            className={isDark ? 'bg-slate-900' : 'bg-slate-50'}
                        >
                            <Background color={isDark ? '#334155' : '#cbd5e1'} gap={24} size={1} />
                            <Controls className={clsx(isDark ? '!bg-slate-800 !border-slate-700' : '!bg-white !border-slate-200')} />
                            <MiniMap
                                className={clsx(isDark ? '!bg-slate-800 !border-slate-700' : '!bg-white !border-slate-200')}
                                zoomable pannable
                                nodeColor={n => TYPE_COLORS[n.data?.type as string]?.stroke || '#94a3b8'}
                            />
                            <Panel position="bottom-left">
                                <FTALegend isDark={isDark} displayMode={displayMode} />
                            </Panel>
                        </ReactFlow>
                    </div>
                ) : (
                    <div className="flex-1 overflow-auto">
                        <ActionPlanTable
                            nodes={nodes}
                            onUpdateNode={(id, data) => setNodes(nds => nds.map(n => n.id === id ? { ...n, data } : n))}
                        />
                    </div>
                )}

                {(selectedNode || selectedEdge) && (
                    <PropertiesPanel
                        selectedNode={selectedNode || null} selectedNodeId={selectedNodeId}
                        selectedEdge={selectedEdge || null} selectedEdgeId={selectedEdgeId}
                        updateSelectedNode={updateSelectedNode} updateSelectedEdge={() => {}}
                        setNodes={setNodes as any} setSelectedNodeId={setSelectedNodeId}
                        setEdges={setEdges} setSelectedEdgeId={setSelectedEdgeId}
                    />
                )}
            </div>
        </div>
    );
}

export default function FTAEditorPage() {
    return <ReactFlowProvider><FTAEditorInner /></ReactFlowProvider>;
}
